import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceArea, Legend,
} from "recharts";
import {
  ArrowLeft, Download, FileText, ChevronDown, ArrowRight, Info,
} from "lucide-react";
import {
  CONVERGENCE, WEALTH_FORECASTS, WEALTH_TOGGLES, sourceById, INHERITANCE_STAGES,
  GAP, RISK_ZONES, FRAMEWORK_LAYERS, AUTHORITY_HIERARCHY, FRAMEWORK_COMPONENTS,
  FOUNDER_OPTIONS, RISK_REGISTER, RISK_CATEGORIES, READINESS_QUESTIONS, READINESS_INVERTED,
  READINESS_LEVELS, TIME_HORIZON, AUDIENCES, SOURCES, METHODOLOGY, CLASSIFICATION_META,
} from "@/lib/indexData";
import { generateExecutiveBriefing, generateReadinessReport } from "@/lib/indexPdf";

/* palette */
const C = { ink: "#1A1D24", ivory: "#F7F5F0", warm: "#FCFBF8", pale: "#ECEAE3", gold: "#B08D57", blue: "#3A5A7A", gray: "#6E7B8B", line: "#E1DDD3" };

const NAV = [
  ["convergence", "Convergence"], ["wealth", "Wealth Transfer"], ["inheritance", "Inheritance Expansion"],
  ["gap", "Governance Gap"], ["risk-map", "Institutional Risk"], ["framework", "EverSoul Framework"],
  ["readiness", "Readiness Assessment"], ["methodology", "Methodology"], ["sources", "Sources"],
];

const Pill = ({ classification }) => {
  const m = CLASSIFICATION_META[classification];
  if (!m) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border" style={{ borderColor: C.line, color: m.color }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />{m.label}
    </span>
  );
};

const Kicker = ({ children }) => (
  <p className="text-[11px] uppercase tracking-[0.35em] mb-4" style={{ color: C.gold }}>{children}</p>
);

const Section = ({ id, kicker, title, intro, children }) => (
  <section id={id} data-testid={`index-section-${id}`} className="scroll-mt-24 border-t" style={{ borderColor: C.line }}>
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20 lg:py-28">
      {kicker && <Kicker>{kicker}</Kicker>}
      <h2 className="font-serif font-light tracking-tight leading-[1.05] text-[clamp(1.9rem,4vw,3.2rem)] max-w-3xl" style={{ color: C.ink }}>{title}</h2>
      {intro && <p className="mt-6 max-w-2xl text-[15px] leading-relaxed" style={{ color: C.gray }}>{intro}</p>}
      <div className="mt-12">{children}</div>
    </div>
  </section>
);

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border bg-white/95 px-3 py-2 text-xs shadow-sm" style={{ borderColor: C.line }}>
      <p className="font-medium mb-1" style={{ color: C.ink }}>{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color || C.gray }}>{p.name}: <span style={{ color: C.ink }}>{p.value}</span></p>
      ))}
    </div>
  );
};

const Drawer = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md" style={{ borderColor: C.line }}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`drawer-${label.replace(/\s+/g, "-").toLowerCase()}`}
        className="w-full flex items-center justify-between px-4 py-3 text-left text-[13px] tracking-wide" style={{ color: C.ink }}>
        <span className="inline-flex items-center gap-2"><Info className="w-3.5 h-3.5" style={{ color: C.gold }} />{label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <div className="grid transition-[grid-template-rows] duration-400" style={{ gridTemplateRows: open ? "1fr" : "0fr" }}>
        <div className="overflow-hidden"><div className="px-4 pb-4 text-[13px] leading-relaxed" style={{ color: C.gray }}>{children}</div></div>
      </div>
    </div>
  );
};

/* ── Converging forces mini visualization (hero) ── */
const ConvergeViz = () => {
  const forces = ["Artificial intelligence", "Wealth transfer", "Digital identity", "Trust & fiduciary governance"];
  const pos = [{ x: 30, y: 26 }, { x: 250, y: 26 }, { x: 30, y: 190 }, { x: 250, y: 190 }];
  return (
    <div className="relative w-full">
      <svg viewBox="0 0 320 240" className="w-full h-auto">
        {pos.map((p, i) => (
          <line key={i} x1={p.x + 30} y1={p.y + 10} x2={160} y2={120} stroke={C.gold} strokeWidth="0.8" strokeDasharray="3 4" opacity="0.6">
            <animate attributeName="opacity" values="0.2;0.7;0.2" dur="4s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
          </line>
        ))}
        <circle cx="160" cy="120" r="7" fill={C.gold}>
          <animate attributeName="r" values="6;9;6" dur="3.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="160" cy="120" r="26" fill="none" stroke={C.gold} strokeWidth="0.6" opacity="0.4" />
      </svg>
      {pos.map((p, i) => (
        <span key={i} className="absolute text-[10px] uppercase tracking-[0.15em] max-w-[110px]"
          style={{ color: C.gray, left: `${(p.x / 320) * 100}%`, top: `${(p.y / 240) * 100}%` }}>{forces[i]}</span>
      ))}
      <span className="absolute left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.15em] text-center max-w-[130px]"
        style={{ color: C.ink, bottom: "-6%" }}>Governed personal AI inheritance</span>
    </div>
  );
};

export default function IntelligenceIndex() {
  const [audience, setAudience] = useState(AUDIENCES[0]);
  const [scenario, setScenario] = useState("expected");
  const [wealthFilter, setWealthFilter] = useState("global");
  const [stage, setStage] = useState(7);
  const [zone, setZone] = useState(RISK_ZONES[3]);
  const [founder, setFounder] = useState([]);
  const [riskCat, setRiskCat] = useState("All");
  const [answers, setAnswers] = useState(Array(12).fill(null));
  const [result, setResult] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const convData = useMemo(() => {
    const s = CONVERGENCE.scenarios[scenario];
    return CONVERGENCE.years.map((y, i) => ({
      year: y, aiCapability: s.aiCapability[i], personalAiPersistence: s.personalAiPersistence[i],
      wealthTransfer: s.wealthTransfer[i], governanceImportance: s.governanceImportance[i],
    }));
  }, [scenario]);

  const gapData = useMemo(() => GAP.years.map((y, i) => ({
    year: y, gov: GAP.governanceReadiness[i], gapBand: Math.max(0, GAP.aiCapability[i] - GAP.governanceReadiness[i]), ai: GAP.aiCapability[i],
  })), []);

  const wealth = WEALTH_FORECASTS.filter((f) => f.categories.includes(wealthFilter));
  const risks = riskCat === "All" ? RISK_REGISTER : RISK_REGISTER.filter((r) => r.cat === riskCat);

  const founderSummary = useMemo(() => {
    if (!founder.length) return null;
    const parts = FOUNDER_OPTIONS.filter((o) => founder.includes(o.id)).map((o) => o.summary);
    return `This personal AI would ${parts.slice(0, -1).join(", ")}${parts.length > 1 ? ", and " : ""}${parts[parts.length - 1]}. In all cases, an authorized human fiduciary retains legal authority, and every interaction remains subordinate to valid legal instruments.`;
  }, [founder]);

  const scoreReadiness = () => {
    if (answers.some((a) => a === null)) { toast.error("Please answer all 12 questions."); return; }
    let score = 0;
    answers.forEach((a, i) => { const yes = a === true; score += (READINESS_INVERTED.includes(i) ? !yes : yes) ? 1 : 0; });
    const level = [...READINESS_LEVELS].reverse().find((l) => score >= l.min);
    const missing = [];
    answers.forEach((a, i) => { const bad = READINESS_INVERTED.includes(i) ? a === true : a === false; if (bad) missing.push(READINESS_QUESTIONS[i]); });
    const res = {
      score, level: level.label, note: level.note,
      risks: score < 7 ? ["AI guidance mistaken for legal authority", "Undefined ownership of the AI identity", "No permanent retirement process"] : ["Residual gaps in vendor continuity or auditability"],
      missing: missing.slice(0, 6),
      steps: ["Define ownership & consent for any personal-AI identity", "Add trust/estate language for access, suspension, and retirement", "Establish authentication, audit, and correction procedures", "Confirm human legal authority supersedes all AI output"],
    };
    setResult(res);
    setTimeout(() => document.getElementById("readiness-result")?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  const btn = "inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-[13px] tracking-wide transition-colors duration-300";

  return (
    <div style={{ background: C.ivory, color: C.ink }} className="min-h-screen font-sans">
      {/* top chrome */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: "rgba(247,245,240,0.85)", borderColor: C.line }}>
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="index-home-link" className="inline-flex items-center gap-2 text-[13px]" style={{ color: C.ink }}>
            <ArrowLeft className="w-4 h-4" /><span className="font-serif text-lg">EverSoul</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-6 overflow-x-auto">
            {NAV.map(([id, label]) => (
              <a key={id} href={`#${id}`} data-testid={`index-nav-${id}`} className="text-[12px] tracking-wide whitespace-nowrap hover:opacity-100 opacity-70 transition-opacity" style={{ color: C.ink }}>{label}</a>
            ))}
          </nav>
          <button onClick={generateExecutiveBriefing} data-testid="index-header-pdf" className="text-[12px] tracking-wide inline-flex items-center gap-2" style={{ color: C.gold }}>
            <Download className="w-3.5 h-3.5" /> Briefing
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b" style={{ borderColor: C.line }}>
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Kicker>EverSoul Legacy Intelligence Index 2026</Kicker>
            <h1 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2.4rem,5.4vw,4.6rem)]">
              The next great inheritance challenge is identity itself.
            </h1>
            <p className="mt-4 font-serif italic text-[clamp(1.1rem,2vw,1.5rem)]" style={{ color: C.blue }}>
              Legacy AI is becoming technically inevitable. Governance is not.
            </p>
            <p className="mt-6 max-w-xl text-[15px] leading-relaxed" style={{ color: C.gray }}>
              As artificial intelligence becomes capable of preserving voice, likeness, memory, values, judgment, and personal history,
              institutions will need new systems for consent, access, fidelity, fiduciary control, inheritance conditions, and permanent retirement.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#convergence" data-testid="hero-explore-index" className={btn} style={{ background: C.ink, color: C.ivory }}>Explore the Index</a>
              <button onClick={generateExecutiveBriefing} data-testid="hero-download-briefing" className={btn} style={{ border: `1px solid ${C.ink}`, color: C.ink }}>
                <FileText className="w-4 h-4" /> Download the Executive Briefing
              </button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border p-8" style={{ borderColor: C.line, background: C.warm }}>
              <p className="text-[10px] uppercase tracking-[0.25em] mb-6" style={{ color: C.gray }}>Four forces converging</p>
              <ConvergeViz />
            </div>
          </div>
        </div>
      </section>

      {/* THESIS */}
      <section className="border-b" style={{ borderColor: C.line, background: C.warm }}>
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <h2 className="font-serif font-light leading-[1.1] text-[clamp(1.8rem,3.4vw,2.8rem)]">
                The financial inheritance system is mature.<br />
                <span style={{ color: C.gold }}>The personal AI inheritance system barely exists.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-5 text-[15px] leading-relaxed" style={{ color: C.gray }}>
              <p>Artificial intelligence is moving from generating content to representing individuals — through voice, likeness, memory, values, communication style, and increasingly persistent personal context. At the same time, trillions of dollars in wealth, control, responsibility, and family stewardship are moving between generations.</p>
              <p style={{ color: C.ink }}>The institutional question is no longer only: what happens to a person's assets? It is also: what happens to the intelligent representation of that person?</p>
            </div>
          </div>

          {/* Audience selector */}
          <div className="mt-14 pt-8 border-t" style={{ borderColor: C.line }}>
            <p className="text-[11px] uppercase tracking-[0.25em] mb-4" style={{ color: C.gray }}>Read this index as a…</p>
            <div className="flex flex-wrap gap-2" data-testid="audience-selector">
              {AUDIENCES.map((a) => (
                <button key={a.id} onClick={() => setAudience(a)} data-testid={`audience-${a.id}`}
                  className="text-[12px] tracking-wide px-4 py-2 rounded-full border transition-colors"
                  style={audience.id === a.id ? { background: C.ink, color: C.ivory, borderColor: C.ink } : { borderColor: C.line, color: C.ink }}>
                  {a.label}
                </button>
              ))}
            </div>
            <p className="mt-5 max-w-3xl text-[14px] leading-relaxed" data-testid="audience-lens" style={{ color: C.ink }}>
              <span style={{ color: C.gold }}>{audience.label} lens — </span>{audience.lens}
            </p>
          </div>
        </div>
      </section>

      {/* 1. CONVERGENCE */}
      <Section id="convergence" kicker="Section 01 · The convergence" title="Four forces are converging"
        intro="Artificial intelligence, personal data, family wealth, and identity governance are progressing on different timelines. The institutional risk emerges when technological capability advances faster than the legal, fiduciary, and operational systems intended to control it.">
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="scenario-selector">
          {["conservative", "expected", "accelerated"].map((s) => (
            <button key={s} onClick={() => setScenario(s)} data-testid={`scenario-${s}`}
              className="text-[12px] capitalize px-4 py-2 rounded-full border transition-colors"
              style={scenario === s ? { background: C.gold, color: "#fff", borderColor: C.gold } : { borderColor: C.line, color: C.ink }}>
              {s} scenario
            </button>
          ))}
        </div>
        <div className="rounded-lg border p-4 lg:p-6" style={{ borderColor: C.line, background: C.warm }}>
          <ResponsiveContainer width="100%" height={380}>
            <LineChart data={convData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={C.line} vertical={false} />
              <XAxis dataKey="year" stroke={C.gray} fontSize={11} tickLine={false} />
              <YAxis stroke={C.gray} fontSize={11} tickLine={false} domain={[0, 100]} label={{ value: "Index (0–100)", angle: -90, position: "insideLeft", fill: C.gray, fontSize: 10 }} />
              <Tooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceArea x1={2025} x2={2050} fill={C.gold} fillOpacity={0.05} />
              {CONVERGENCE.series.map((s) => (
                <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2}
                  strokeDasharray={s.classification === "modeled" ? "5 4" : undefined} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-4">
            <Pill classification="published" /><Pill classification="calculated" /><Pill classification="modeled" />
            <span className="text-[11px]" style={{ color: C.gray }}>Solid = published/calculated · Dotted = modeled · Shaded region = forecast (post-2025)</span>
          </div>
        </div>
        <div className="mt-6 rounded-md border-l-2 pl-5 py-3" style={{ borderColor: C.gold }}>
          <p className="text-[15px] leading-relaxed" style={{ color: C.ink }}>
            The opportunity is not to predict the exact size of legacy AI. It is to recognize that the institutions governing inheritance will increasingly encounter personal AI.
          </p>
        </div>
      </Section>

      {/* 2. WEALTH TRANSFER */}
      <Section id="wealth" kicker="Section 02 · Wealth transfer" title="The largest intergenerational transfer of wealth and responsibility is underway"
        intro="Published estimates are shown separately when methodologies or geographic scopes differ — they are not merged into a single artificial number.">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="wealth-toggles">
          {WEALTH_TOGGLES.map((t) => (
            <button key={t.key} onClick={() => setWealthFilter(t.key)} data-testid={`wealth-${t.key}`}
              className="text-[12px] px-4 py-2 rounded-full border transition-colors"
              style={wealthFilter === t.key ? { background: C.ink, color: C.ivory, borderColor: C.ink } : { borderColor: C.line, color: C.ink }}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-5" data-testid="wealth-forecasts">
          {wealth.length === 0 && <p className="text-[14px]" style={{ color: C.gray }}>No published estimate in this view.</p>}
          {wealth.map((f) => {
            const s = sourceById(f.sourceId);
            return (
              <div key={f.id} className="rounded-lg border p-6" style={{ borderColor: C.line, background: C.warm }}>
                <div className="flex items-start justify-between gap-3">
                  <p className="font-serif text-[clamp(1.5rem,2.6vw,2.1rem)]" style={{ color: C.ink }}>{f.total}</p>
                  <Pill classification={s.classification} />
                </div>
                <p className="mt-2 text-[13px]" style={{ color: C.gray }}>{f.geography} · {f.forecastPeriod}</p>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: C.ink }}>{f.note}</p>
                <div className="mt-4 pt-4 border-t text-[12px]" style={{ borderColor: C.line, color: C.gray }}>
                  <p>{s.org} · {s.title} ({s.year})</p>
                  <p className="mt-1">Methodology: {s.methodology}</p>
                  <a href={s.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1" style={{ color: C.blue }}>Source ↗</a>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-6 rounded-md border-l-2 pl-5 py-3" style={{ borderColor: C.gold }}>
          <p className="text-[15px] leading-relaxed" style={{ color: C.ink }}>
            The significance is not that EverSoul captures a percentage of transferred assets. It is that the banks, trusts, family offices, attorneys, and advisors overseeing this transfer will increasingly encounter digital identity, personal AI, likeness, voice, memory, and posthumous-access questions.
          </p>
        </div>
      </Section>

      {/* 3. INHERITANCE EXPANSION */}
      <Section id="inheritance" kicker="Section 03 · Inheritance expansion" title="What society considers inheritable is expanding"
        intro="Estate planning has developed systems for transferring nearly every major form of value. It has not yet developed mature systems for inheriting an interactive representation of the individual.">
        <div className="flex gap-1.5 overflow-x-auto pb-4" data-testid="inheritance-stages">
          {INHERITANCE_STAGES.map((st, i) => (
            <button key={st.k} onClick={() => setStage(i)} data-testid={`stage-${i}`}
              className="flex-1 min-w-[92px] rounded-md border px-2 py-3 text-[11px] leading-tight transition-colors"
              style={stage === i ? { background: C.ink, color: C.ivory, borderColor: C.ink } : { borderColor: C.line, color: C.gray }}>
              <span className="block text-[10px] mb-1" style={{ color: stage === i ? C.gold : C.gold }}>{String(i + 1).padStart(2, "0")}</span>
              {st.k}
            </button>
          ))}
        </div>
        <div className="rounded-lg border p-7" style={{ borderColor: C.line, background: C.warm }}>
          <h3 className="font-serif text-[clamp(1.4rem,2.4vw,2rem)]" style={{ color: C.ink }}>{INHERITANCE_STAGES[stage].k}</h3>
          <p className="mt-3 text-[15px] leading-relaxed max-w-2xl" style={{ color: C.gray }}>{INHERITANCE_STAGES[stage].body}</p>
          {INHERITANCE_STAGES[stage].detail && (
            <div className="mt-5 flex flex-wrap gap-2">
              {INHERITANCE_STAGES[stage].detail.map((d) => (
                <span key={d} className="text-[12px] px-3 py-1.5 rounded-full border" style={{ borderColor: C.line, color: C.ink }}>{d}</span>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* 4. GOVERNANCE GAP */}
      <Section id="gap" kicker="Section 04 · Governance gap" title="AI capability is advancing faster than inheritance governance"
        intro="Two EverSoul analytical indices — not externally published market indices. The shaded band is the widening distance between what personal AI can do and what institutions are prepared to govern.">
        <div className="rounded-lg border p-4 lg:p-6" style={{ borderColor: C.line, background: C.warm }}>
          <ResponsiveContainer width="100%" height={360}>
            <AreaChart data={gapData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={C.line} vertical={false} />
              <XAxis dataKey="year" stroke={C.gray} fontSize={11} tickLine={false} />
              <YAxis stroke={C.gray} fontSize={11} tickLine={false} domain={[0, 100]} />
              <Tooltip content={<ChartTip />} />
              <Area dataKey="gov" stackId="1" stroke="none" fill="transparent" name="_" legendType="none" />
              <Area dataKey="gapBand" stackId="1" stroke="none" fill={C.gold} fillOpacity={0.18} name="Personal AI Governance Gap" />
              <Line type="monotone" dataKey="ai" data={gapData} stroke={C.blue} strokeWidth={2} strokeDasharray="5 4" dot={false} name="Personal AI capability" />
              <Line type="monotone" dataKey="gov" stroke={C.ink} strokeWidth={2} strokeDasharray="5 4" dot={false} name="Institutional governance readiness" />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-4"><Pill classification="modeled" />
            <span className="text-[11px]" style={{ color: C.gray }}>Both indices are EverSoul analytical models (0–100).</span></div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <div className="rounded-md border p-5" style={{ borderColor: C.line }}>
            <p className="text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: C.blue }}>Capability index considers</p>
            <div className="flex flex-wrap gap-1.5">{GAP.aiFactors.map((f) => <span key={f} className="text-[11px] px-2.5 py-1 rounded-full border" style={{ borderColor: C.line, color: C.gray }}>{f}</span>)}</div>
          </div>
          <div className="rounded-md border p-5" style={{ borderColor: C.line }}>
            <p className="text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: C.ink }}>Readiness index considers</p>
            <div className="flex flex-wrap gap-1.5">{GAP.govFactors.map((f) => <span key={f} className="text-[11px] px-2.5 py-1 rounded-full border" style={{ borderColor: C.line, color: C.gray }}>{f}</span>)}</div>
          </div>
        </div>
        <div className="mt-6"><Drawer label="Methodology — how these indices are scored">
          <p>{METHODOLOGY.scoring}</p>
          <p className="mt-2">{METHODOLOGY.confidence}</p>
          <p className="mt-2">{METHODOLOGY.limitations} Update date: {SOURCES.find((s) => s.id === "eversoul-index-2026").accessed}.</p>
        </Drawer></div>
      </Section>

      {/* 5. RISK MAP */}
      <Section id="risk-map" kicker="Section 05 · Institutional risk map" title="The risk changes as personal AI becomes more capable and more consequential"
        intro="Horizontal: increasing personal-AI sophistication. Vertical: increasing financial, emotional, and family significance.">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div>
            <div className="relative aspect-square rounded-lg border overflow-hidden" style={{ borderColor: C.line, background: C.warm }} data-testid="risk-matrix">
              <div className="grid grid-cols-2 grid-rows-2 h-full">
                {[RISK_ZONES[2], RISK_ZONES[3], RISK_ZONES[0], RISK_ZONES[1]].map((z) => (
                  <button key={z.id} onClick={() => setZone(z)} data-testid={`zone-${z.id}`}
                    className="relative border p-4 text-left transition-colors"
                    style={{ borderColor: C.line, background: zone.id === z.id ? (z.required ? C.gold : C.ink) : "transparent", color: zone.id === z.id ? "#fff" : C.ink }}>
                    <span className="text-[13px] font-medium leading-tight">{z.title}</span>
                    {z.required && <span className="block text-[10px] uppercase tracking-[0.15em] mt-1.5 opacity-90">Formal governance required</span>}
                  </button>
                ))}
              </div>
              <span className="absolute -bottom-0 left-1/2 -translate-x-1/2 translate-y-6 text-[10px] uppercase tracking-[0.15em]" style={{ color: C.gray }}>Sophistication →</span>
              <span className="absolute top-1/2 -left-2 -translate-x-full -translate-y-1/2 -rotate-90 text-[10px] uppercase tracking-[0.15em] whitespace-nowrap" style={{ color: C.gray }}>Significance →</span>
            </div>
          </div>
          <div className="rounded-lg border p-7" style={{ borderColor: C.line }}>
            <div className="flex items-center gap-3"><h3 className="font-serif text-[clamp(1.4rem,2.4vw,2rem)]" style={{ color: C.ink }}>{zone.title}</h3>
              {zone.required && <Pill classification="framework" />}</div>
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {zone.examples.map((e) => <li key={e} className="text-[14px] flex items-start gap-2" style={{ color: C.gray }}><span style={{ color: C.gold }}>—</span>{e}</li>)}
            </ul>
          </div>
        </div>
        <div className="mt-6 rounded-md border-l-2 pl-5 py-3" style={{ borderColor: C.gold }}>
          <p className="text-[15px] leading-relaxed" style={{ color: C.ink }}>The more an AI representation can influence future decisions, the less appropriate it becomes to treat it as ordinary digital content.</p>
        </div>
      </Section>

      {/* 6. FRAMEWORK */}
      <Section id="framework" kicker="Section 06 · EverSoul framework" title="EverSoul is designed as governance infrastructure for personal AI continuity"
        intro="Information passes through defined layers, with legal authority always retained by humans.">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-3" data-testid="framework-layers">
            {FRAMEWORK_LAYERS.map((l, i) => (
              <div key={l.n} className="rounded-lg border p-5 flex gap-5" style={{ borderColor: C.line, background: i === 1 ? "rgba(58,90,122,0.06)" : C.warm }}>
                <span className="font-serif text-2xl shrink-0" style={{ color: C.gold }}>0{l.n}</span>
                <div><p className="font-medium text-[15px]" style={{ color: C.ink }}>{l.title}</p>
                  <p className="mt-1 text-[14px] leading-relaxed" style={{ color: C.gray }}>{l.body}</p></div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-lg border p-6" style={{ borderColor: C.line }}>
              <p className="text-[11px] uppercase tracking-[0.2em] mb-4" style={{ color: C.gold }}>Authority hierarchy</p>
              <ol className="space-y-2">
                {AUTHORITY_HIERARCHY.map((a, i) => (
                  <li key={a} className="flex items-center gap-3 text-[14px]" style={{ color: C.ink }}>
                    <span className="text-[11px] w-5" style={{ color: C.gray }}>{i + 1}</span>{a}
                  </li>
                ))}
              </ol>
              <p className="mt-6 font-serif italic text-lg" style={{ color: C.blue }}>The AI is never the legal authority.</p>
            </div>
            <div className="mt-4 rounded-lg border p-6" style={{ borderColor: C.line }}>
              <p className="text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: C.gray }}>Governance components</p>
              <div className="flex flex-wrap gap-1.5">{FRAMEWORK_COMPONENTS.map((c) => <span key={c} className="text-[11px] px-2.5 py-1 rounded-full border" style={{ borderColor: C.line, color: C.ink }}>{c}</span>)}</div>
            </div>
          </div>
        </div>
      </Section>

      {/* 7. FOUNDER SCENARIO */}
      <Section id="founder" kicker="Section 07 · Family founder scenario" title="What might a family founder want to control?"
        intro="Select the instructions a founder might set. EverSoul assembles a governance summary — always subordinate to human fiduciary authority.">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="grid gap-2" data-testid="founder-options">
            {FOUNDER_OPTIONS.map((o) => {
              const on = founder.includes(o.id);
              return (
                <button key={o.id} onClick={() => setFounder((p) => on ? p.filter((x) => x !== o.id) : [...p, o.id])} data-testid={`founder-${o.id}`}
                  className="flex items-center gap-3 rounded-md border px-4 py-3 text-left text-[14px] transition-colors"
                  style={on ? { borderColor: C.gold, background: "rgba(176,141,87,0.08)", color: C.ink } : { borderColor: C.line, color: C.gray }}>
                  <span className="h-4 w-4 rounded-sm border flex items-center justify-center shrink-0" style={{ borderColor: on ? C.gold : C.line, background: on ? C.gold : "transparent" }}>
                    {on && <span className="text-white text-[10px]">✓</span>}</span>{o.label}
                </button>
              );
            })}
          </div>
          <div className="rounded-lg border p-7 self-start" style={{ borderColor: C.line, background: C.warm }} data-testid="founder-summary">
            <p className="text-[11px] uppercase tracking-[0.2em] mb-4" style={{ color: C.gold }}>Generated governance summary</p>
            {founderSummary
              ? <p className="text-[15px] leading-relaxed" style={{ color: C.ink }}>{founderSummary}</p>
              : <p className="text-[14px] italic" style={{ color: C.gray }}>Select one or more instructions to generate a summary.</p>}
          </div>
        </div>
      </Section>

      {/* 9. RISK REGISTER */}
      <Section id="risk-register" kicker="Section 08 · Personal AI risk register" title="Emerging risks require identifiable controls"
        intro="Filter by domain. Risk frameworks referenced include the NIST AI Risk Management Framework. EverSoul does not claim to eliminate all risks.">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="risk-filters">
          {["All", ...RISK_CATEGORIES].map((c) => (
            <button key={c} onClick={() => setRiskCat(c)} data-testid={`riskcat-${c}`}
              className="text-[12px] px-3.5 py-1.5 rounded-full border transition-colors"
              style={riskCat === c ? { background: C.ink, color: C.ivory, borderColor: C.ink } : { borderColor: C.line, color: C.ink }}>{c}</button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-4" data-testid="risk-register">
          {risks.map((r) => (
            <div key={r.name} className="rounded-lg border p-5" style={{ borderColor: C.line, background: C.warm }}>
              <div className="flex items-start justify-between gap-3">
                <p className="font-medium text-[15px]" style={{ color: C.ink }}>{r.name}</p>
                <span className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.gold }}>{r.cat}</span>
              </div>
              <div className="flex gap-4 mt-3 text-[12px]" style={{ color: C.gray }}>
                <span>Likelihood: <b style={{ color: C.ink }}>{r.likelihood}</b></span>
                <span>Severity: <b style={{ color: C.ink }}>{r.severity}</b></span>
                <span>Confidence: <b style={{ color: C.ink }}>{r.confidence}</b></span>
              </div>
              <p className="mt-3 text-[13px]" style={{ color: C.gray }}>Affected: {r.parties}</p>
              <p className="mt-2 text-[13px]" style={{ color: C.ink }}><span style={{ color: C.gold }}>Control — </span>{r.control}</p>
              <p className="mt-1 text-[12px]" style={{ color: C.gray }}>EverSoul: {r.capability}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 10. READINESS */}
      <Section id="readiness" kicker="Section 09 · Institutional readiness" title="Is your organization prepared for personal AI inheritance?"
        intro="Twelve questions. Your responses are scored locally in your browser and never stored.">
        <div className="space-y-2" data-testid="readiness-questions">
          {READINESS_QUESTIONS.map((q, i) => (
            <div key={i} className="flex items-center justify-between gap-4 rounded-md border px-4 py-3" style={{ borderColor: C.line, background: C.warm }}>
              <p className="text-[14px]" style={{ color: C.ink }}><span style={{ color: C.gold }}>{String(i + 1).padStart(2, "0")}</span> · {q}</p>
              <div className="flex gap-2 shrink-0">
                {[["Yes", true], ["No", false]].map(([lbl, val]) => (
                  <button key={lbl} onClick={() => setAnswers((a) => { const n = [...a]; n[i] = val; return n; })} data-testid={`q${i}-${lbl.toLowerCase()}`}
                    className="text-[12px] px-4 py-1.5 rounded-full border transition-colors"
                    style={answers[i] === val ? { background: C.ink, color: C.ivory, borderColor: C.ink } : { borderColor: C.line, color: C.gray }}>{lbl}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button onClick={scoreReadiness} data-testid="readiness-submit" className={`${btn} mt-6`} style={{ background: C.gold, color: "#fff" }}>Score my readiness <ArrowRight className="w-4 h-4" /></button>

        {result && (
          <div id="readiness-result" data-testid="readiness-result" className="mt-8 rounded-lg border p-7" style={{ borderColor: C.gold, background: "rgba(176,141,87,0.06)" }}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h3 className="font-serif text-[clamp(1.6rem,3vw,2.4rem)]" style={{ color: C.ink }}>{result.level}</h3>
              <span className="text-[14px]" style={{ color: C.gray }}>Score {result.score} / 12</span>
            </div>
            <p className="mt-2 text-[15px]" style={{ color: C.gray }}>{result.note}</p>
            {result.missing.length > 0 && (
              <div className="mt-5"><p className="text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: C.gold }}>Missing controls</p>
                <ul className="grid sm:grid-cols-2 gap-1.5">{result.missing.map((m) => <li key={m} className="text-[13px] flex gap-2" style={{ color: C.ink }}><span style={{ color: C.gold }}>—</span>{m}</li>)}</ul></div>
            )}
            <div className="mt-5"><p className="text-[11px] uppercase tracking-[0.2em] mb-2" style={{ color: C.gold }}>Recommended next steps</p>
              <ul className="space-y-1.5">{result.steps.map((s) => <li key={s} className="text-[13px] flex gap-2" style={{ color: C.ink }}><span style={{ color: C.gold }}>→</span>{s}</li>)}</ul></div>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => generateReadinessReport(result)} data-testid="readiness-pdf" className={btn} style={{ background: C.ink, color: C.ivory }}><Download className="w-4 h-4" /> Download result (PDF)</button>
              <a href="#contact" className={btn} style={{ border: `1px solid ${C.ink}`, color: C.ink }}>Request a conversation</a>
            </div>
            <p className="mt-5 text-[11px] leading-relaxed" style={{ color: C.gray }}>This assessment is informational and does not constitute legal, financial, fiduciary, or estate-planning advice.</p>
          </div>
        )}
      </Section>

      {/* 11. TIME HORIZON */}
      <Section id="horizon" kicker="Section 10 · Time horizon" title="A possible institutional development path"
        intro="Stages beyond current published forecasts are labeled EverSoul modeled scenarios.">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="time-horizon">
          {TIME_HORIZON.map((t) => (
            <div key={t.period} className="rounded-lg border p-5" style={{ borderColor: C.line, background: C.warm }}>
              <p className="text-[11px] uppercase tracking-[0.18em]" style={{ color: C.gray }}>{t.period}</p>
              <p className="font-serif text-[1.3rem] mt-1 mb-3" style={{ color: C.ink }}>{t.label}</p>
              <div className="mb-3"><Pill classification={t.classification} /></div>
              <ul className="space-y-1.5">{t.items.map((it) => <li key={it} className="text-[13px] flex gap-2" style={{ color: C.gray }}><span style={{ color: C.gold }}>—</span>{it}</li>)}</ul>
            </div>
          ))}
        </div>
      </Section>

      {/* 8/12. METHODOLOGY + SOURCES */}
      <Section id="methodology" kicker="Methodology" title="How this index is built"
        intro={METHODOLOGY.summary}>
        <div className="grid md:grid-cols-3 gap-4">
          {[["Scoring", METHODOLOGY.scoring], ["Confidence", METHODOLOGY.confidence], ["Limitations", METHODOLOGY.limitations]].map(([t, b]) => (
            <div key={t} className="rounded-lg border p-6" style={{ borderColor: C.line }}>
              <p className="text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: C.gold }}>{t}</p>
              <p className="text-[14px] leading-relaxed" style={{ color: C.gray }}>{b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="sources" kicker="Sources" title="Source transparency"
        intro="Every figure is attributed. Classifications distinguish published data, calculated data, EverSoul modeled scenarios, and conceptual frameworks.">
        <div className="space-y-3" data-testid="sources-list">
          {SOURCES.map((s) => (
            <div key={s.id} className="rounded-lg border p-5" style={{ borderColor: C.line }}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><p className="font-medium text-[15px]" style={{ color: C.ink }}>{s.org}</p>
                  <p className="text-[13px]" style={{ color: C.gray }}>{s.title} ({s.year}) · {s.geography}</p></div>
                <Pill classification={s.classification} />
              </div>
              <p className="mt-3 text-[13px]" style={{ color: C.gray }}>Methodology: {s.methodology}</p>
              <p className="mt-1 text-[13px]" style={{ color: C.gray }}>Limitations: {s.limitations}</p>
              <div className="mt-2 flex items-center gap-4 text-[12px]">
                <a href={s.url} target="_blank" rel="noreferrer" style={{ color: C.blue }}>Source ↗</a>
                <span style={{ color: C.gray }}>Accessed {s.accessed}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* CONTACT / CTA */}
      <section id="contact" className="border-t" style={{ borderColor: C.line, background: C.ink, color: C.ivory }}>
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-24">
          <p className="text-[11px] uppercase tracking-[0.35em] mb-6" style={{ color: C.gold }}>Partnership</p>
          <h2 className="font-serif font-light leading-[1.05] text-[clamp(2rem,4.4vw,3.6rem)] max-w-3xl">
            The institutions that govern inheritance will soon need to govern identity.
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed" style={{ color: "rgba(247,245,240,0.7)" }}>
            EverSoul is exploring partnerships with private banks, trust companies, family offices, estate professionals, insurers, AI companies, and institutions preparing for the future of personal identity inheritance.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a href="mailto:greg@eversoul.world?subject=EverSoul%20institutional%20briefing" data-testid="index-contact-btn" className={btn} style={{ background: C.ivory, color: C.ink }}>Start a Conversation</a>
            <button onClick={generateExecutiveBriefing} className={btn} style={{ border: "1px solid rgba(247,245,240,0.4)", color: C.ivory }}><Download className="w-4 h-4" /> Download the Executive Briefing</button>
          </div>
          <p className="mt-14 max-w-3xl text-[12px] leading-relaxed" style={{ color: "rgba(247,245,240,0.55)" }}>
            This index is provided for research and informational purposes and does not constitute legal, financial, tax, fiduciary, investment, or estate-planning advice. Forecasts and scenarios are inherently uncertain, and EverSoul modeled scenarios are not predictions of guaranteed outcomes. Third-party data remains subject to the methodologies and limitations of its original sources. Institutional, legal, and regulatory developments may differ by jurisdiction. Personal AI must remain subordinate to valid legal instruments and authorized human fiduciaries.
          </p>
          <p className="mt-10 font-serif italic text-[clamp(1.1rem,2.2vw,1.6rem)]" style={{ color: C.ivory }}>
            AI is creating persistent representations of people before society has established who owns them, who may access them, who may modify them, and who has the authority to end them. EverSoul exists to help define and govern that emerging category.
          </p>
          <Link to="/" className="mt-10 inline-flex items-center gap-2 text-[13px]" style={{ color: "rgba(247,245,240,0.7)" }}><ArrowLeft className="w-4 h-4" /> Back to EverSoul.world</Link>
        </div>
      </section>
      <Toaster position="bottom-right" />
    </div>
  );
}
